/**
 * Asset List component for displaying list of gold assets
 * Supports pagination for large lists
 */

import { useState, useMemo } from 'react'
import { AssetCard } from './AssetCard'
import { Pagination } from '../common/Pagination'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent } from '@/components/ui/card'
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { IconPackage } from '@tabler/icons-react'
import type { GoldAsset } from '@/types/asset'
import type { GoldPriceHistory } from '@/types/price'

interface AssetListProps {
  assets: GoldAsset[]
  currentPrices?: Map<number, GoldPriceHistory>
  onEdit?: (asset: GoldAsset) => void
  onDelete?: (asset: GoldAsset) => void
  onSell?: (asset: GoldAsset) => void
  isLoading?: boolean
  itemsPerPage?: number
}

export function AssetList({
  assets,
  currentPrices,
  onEdit,
  onDelete,
  onSell,
  isLoading,
  itemsPerPage = 12,
}: AssetListProps) {
  const [currentPage, setCurrentPage] = useState(1)

  const paginatedAssets = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return assets.slice(startIndex, endIndex)
  }, [assets, currentPage, itemsPerPage])

  const totalPages = Math.ceil(assets.length / itemsPerPage)

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Card key={i}>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-4">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <div className="grid grid-cols-2 gap-4">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
                <Skeleton className="h-10 w-full" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (assets.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <IconPackage className="h-12 w-12" stroke={1.5} />
          </EmptyMedia>
          <EmptyTitle>Chưa có tài sản nào</EmptyTitle>
          <EmptyDescription>
            Hãy thêm tài sản đầu tiên của bạn để bắt đầu theo dõi
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        {paginatedAssets.map((asset) => {
          const currentPrice = currentPrices?.get(asset.goldTypeId)?.sellPrice
          return (
            <AssetCard
              key={asset.id}
              asset={asset}
              currentPrice={currentPrice}
              {...(onEdit ? { onEdit } : {})}
              {...(onDelete ? { onDelete } : {})}
              {...(onSell ? { onSell } : {})}
            />
          )
        })}
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          totalItems={assets.length}
        />
      )}
    </>
  )
}
